/**
 * Clear Admin XML Generator - TypeScript Implementation
 * Számlázási XML-ek generálása a webes felületen
 */

import {
  ClearAdminCurrency,
  ClearAdminBillingData,
  ClearAdminCustomerInfo,
  ClearAdminGenerationRequest,
  ClearAdminGeneratorConfig,
  ClearAdminGenerationResult,
  ClearAdminValidationResult,
  ClearAdminOrderData,
  ClearAdminOrderHead,
  ClearAdminOrderItem
} from '../types/clearAdmin';

export class ClearAdminGenerator {
  private config: ClearAdminGeneratorConfig;

  constructor(config?: Partial<ClearAdminGeneratorConfig>) {
    this.config = {
      currency: 'HUF',
      vat_rate: 0.27, // 27% ÁFA Magyarországon
      include_cancelled: false,
      format_version: '2.0',
      encoding: 'UTF-8',
      validation: {
        strict_customer_validation: true,
        require_tax_number: true,
        min_amount_threshold: 0.01
      },
      output: {
        pretty_print: true,
        include_metadata: true,
        filename_pattern: 'import_{currency}_{date}.xml'
      },
      ...config
    };
  }

  /**
   * Fő XML generálás metódus
   */
  generateXML(
    billingData: ClearAdminBillingData[],
    request: ClearAdminGenerationRequest
  ): ClearAdminGenerationResult {
    try {
      // Validáció
      const validation = this.validateRequest(billingData, request);
      if (!validation.isValid) {
        return {
          success: false,
          currency: request.currency,
          generatedAt: new Date().toISOString(),
          errors: validation.errors,
          warnings: validation.warnings
        };
      }

      // Adatok csoportosítása ügyfelenként
      const groupedOrders = this.groupBillingDataByCustomer(billingData);

      // XML generálás
      const xml = this.generateClearAdminXML(groupedOrders, request);

      // Fájlnév generálás
      const fileName = this.generateFileName(request);

      return {
        success: true,
        xml,
        fileName,
        currency: request.currency,
        recordCount: billingData.length,
        totalAmount: validation.totalAmount,
        generatedAt: new Date().toISOString(),
        isValid: true,
        warnings: validation.warnings
      };

    } catch (error) {
      return {
        success: false,
        currency: request.currency,
        generatedAt: new Date().toISOString(),
        errors: [`XML generálási hiba: ${error instanceof Error ? error.message : 'Ismeretlen hiba'}`]
      };
    }
  }

  /**
   * Clear Admin XML struktúra generálása
   */
  private generateClearAdminXML(
    orders: ClearAdminOrderData[],
    request: ClearAdminGenerationRequest
  ): string {
    let xml = '<?xml version="2.0" encoding="UTF-8" standalone="yes" ?>\n';
    xml += '<!DOCTYPE ORDERS>\n';
    xml += '<ORDERS>\n';

    orders.forEach((order, index) => {
      xml += this.generateOrderXML(order, request, index + 1);
    });

    xml += '</ORDERS>\n';

    return this.config.output.pretty_print ? this.prettifyXML(xml) : xml;
  }

  /**
   * Egyes ORDER XML generálása
   */
  private generateOrderXML(
    order: ClearAdminOrderData,
    request: ClearAdminGenerationRequest,
    orderNum: number
  ): string {
    const orderHead = this.generateOrderHead(order, request, orderNum);
    const orderRows = this.generateOrderRows(order, request);

    let xml = '\t<ORDER>\n';
    xml += '\t\t<ORDERHEAD>\n';

    // ORDERHEAD mezők
    Object.entries(orderHead).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        xml += `\t\t\t<${key}>${this.escapeXML(String(value))}</${key}>\n`;
      }
    });

    xml += '\t\t</ORDERHEAD>\n';

    // ORDERROW elemek
    orderRows.forEach(row => {
      xml += '\t\t<ORDERROW>\n';
      Object.entries(row).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          xml += `\t\t\t<${key}>${this.escapeXML(String(value))}</${key}>\n`;
        }
      });
      xml += '\t\t</ORDERROW>\n';
    });

    xml += '\t</ORDER>\n';

    return xml;
  }

  /**
   * ORDERHEAD adatok generálása
   */
  private generateOrderHead(
    order: ClearAdminOrderData,
    request: ClearAdminGenerationRequest,
    orderNum: number
  ): ClearAdminOrderHead {
    const customer = order.customer_info;
    const today = new Date();
    const periodStr = request.month && request.year 
      ? `${request.year}.${request.month.toString().padStart(2, '0')}`
      : today.toISOString().substring(0, 7).replace('-', '.');

    return {
      ORDER_NUM: `ORD-${orderNum.toString().padStart(6, '0')}-${periodStr}`,
      ORDER_DATE: today.toISOString().split('T')[0],
      CURRENCY: request.currency,
      PARTNER_ID: customer.customer_id,
      COMP_NAME: customer.company_name || customer.customer_name,
      COMP_VAT_NUM: customer.tax_number,
      COMP_ADDRESS: customer.address?.street || '',
      COMP_CITY: customer.address?.city || '',
      COMP_ZIP: customer.address?.postalCode || '',
      COMP_COUNTRY: customer.address?.country || 'Hungary',
      COMP_EMAIL: customer.contact_email,
      COMP_PHONE: customer.contact_phone
    };
  }

  /**
   * ORDERROW elemek generálása
   */
  private generateOrderRows(
    order: ClearAdminOrderData,
    request: ClearAdminGenerationRequest
  ): ClearAdminOrderItem[] {
    return order.billing_data.map((billing, index) => {
      const itemNum = (index + 1).toString().padStart(3, '0');
      const vatRate = this.config.vat_rate;
      
      // Valuta konverzió ha szükséges
      const netAmount = request.currency === 'EUR' 
        ? this.convertHUFtoEUR(billing.net_amount)
        : billing.net_amount;
      
      const vatAmount = request.currency === 'EUR'
        ? this.convertHUFtoEUR(billing.vat_amount)
        : billing.vat_amount;

      const grossAmount = request.currency === 'EUR'
        ? this.convertHUFtoEUR(billing.gross_amount)
        : billing.gross_amount;

      const pricePerKwh = request.currency === 'EUR'
        ? this.convertHUFtoEUR(billing.price_per_kwh)
        : billing.price_per_kwh;

      return {
        ITEM_NUM: itemNum,
        ITEM_DESC: `Energia fogyasztás - ${billing.pod_id}`,
        ITEM_QTY: billing.consumption_kwh,
        ITEM_UNIT: 'kWh',
        ITEM_PRICE: pricePerKwh,
        ITEM_VAT_RATE: vatRate * 100, // Százalékban
        ITEM_NET_VALUE: netAmount,
        ITEM_VAT_VALUE: vatAmount,
        ITEM_GROSS_VALUE: grossAmount,
        POD_ID: billing.pod_id,
        BILLING_PERIOD: billing.billing_period,
        CONSUMPTION_KWH: billing.consumption_kwh,
        PROVIDER: billing.provider.toUpperCase()
      };
    });
  }

  /**
   * Számlázási adatok csoportosítása ügyfelenként
   */
  private groupBillingDataByCustomer(billingData: ClearAdminBillingData[]): ClearAdminOrderData[] {
    const grouped = new Map<string, ClearAdminBillingData[]>();

    // Csoportosítás customer_id szerint
    billingData.forEach(billing => {
      const customerId = billing.customer_id;
      if (!grouped.has(customerId)) {
        grouped.set(customerId, []);
      }
      grouped.get(customerId)!.push(billing);
    });

    // ClearAdminOrderData létrehozása
    return Array.from(grouped.entries()).map(([customerId, billings]) => {
      const firstBilling = billings[0];
      
      // Ügyfél információk kinyerése
      const customerInfo: ClearAdminCustomerInfo = {
        customer_id: customerId,
        customer_name: firstBilling.customer_name,
        company_name: firstBilling.customer_name,
        tax_number: firstBilling.tax_number || '',
        address: this.parseAddress(firstBilling.address || ''),
        contact_email: firstBilling.contact_email,
        contact_phone: firstBilling.contact_phone,
        customer_type: firstBilling.tax_number ? 'organization' : 'individual'
      };

      // Összesítések számítása
      const totals = {
        total_consumption: billings.reduce((sum, b) => sum + b.consumption_kwh, 0),
        total_net_amount: billings.reduce((sum, b) => sum + b.net_amount, 0),
        total_vat_amount: billings.reduce((sum, b) => sum + b.vat_amount, 0),
        total_gross_amount: billings.reduce((sum, b) => sum + b.gross_amount, 0)
      };

      return {
        customer_info: customerInfo,
        billing_data: billings,
        totals
      };
    });
  }

  /**
   * Kérelem validálása
   */
  validateRequest(
    billingData: ClearAdminBillingData[],
    request: ClearAdminGenerationRequest
  ): ClearAdminValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];
    const info: string[] = [];

    // Alapvalidációk
    if (!billingData || billingData.length === 0) {
      errors.push('Nincsenek számlázási adatok');
    }

    if (!['HUF', 'EUR'].includes(request.currency)) {
      errors.push('Érvénytelen valuta, csak HUF és EUR támogatott');
    }

    // Adatok validálása
    let totalAmount = 0;
    billingData.forEach((billing, index) => {
      if (!billing.customer_id) {
        errors.push(`${index + 1}. sor: Hiányzó ügyfél azonosító`);
      }
      
      if (!billing.customer_name) {
        errors.push(`${index + 1}. sor: Hiányzó ügyfél név`);
      }

      if (!billing.pod_id) {
        errors.push(`${index + 1}. sor: Hiányzó POD azonosító`);
      }

      if (this.config.validation.require_tax_number && !billing.tax_number) {
        warnings.push(`${index + 1}. sor: Hiányzó adószám (${billing.customer_name})`);
      }

      if (billing.consumption_kwh <= 0) {
        warnings.push(`${index + 1}. sor: Nulla vagy negatív fogyasztás`);
      }

      if (billing.gross_amount < this.config.validation.min_amount_threshold) {
        warnings.push(`${index + 1}. sor: Összeg a minimális küszöb alatt`);
      }

      totalAmount += billing.gross_amount;
    });

    info.push(`${billingData.length} számlázási tétel feldolgozásra kerül`);
    info.push(`Összérték: ${totalAmount.toFixed(2)} ${request.currency}`);

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
      info,
      recordCount: billingData.length,
      totalAmount,
      currency: request.currency
    };
  }

  /**
   * Fájlnév generálása
   */
  generateFileName(request: ClearAdminGenerationRequest): string {
    const date = new Date().toISOString().split('T')[0];
    const monthYear = request.month && request.year 
      ? `${request.year}-${request.month.toString().padStart(2, '0')}`
      : date.substring(0, 7);

    return this.config.output.filename_pattern
      .replace('{currency}', request.currency)
      .replace('{date}', date)
      .replace('{month_year}', monthYear);
  }

  /**
   * HUF -> EUR konverzió (kb. 400 HUF = 1 EUR)
   */
  private convertHUFtoEUR(hufAmount: number): number {
    const rate = 1 / 400; // Egyszerű konverziós ráta
    return Math.round(hufAmount * rate * 100) / 100;
  }

  /**
   * Cím szöveg feldolgozása
   */
  private parseAddress(addressString: string): ClearAdminCustomerInfo['address'] {
    // Egyszerű cím parsing - valós implementációban složitebb lenne
    const parts = addressString.split(',').map(part => part.trim());
    
    return {
      street: parts[0] || '',
      city: parts[1] || '',
      postalCode: parts[2] || '',
      country: 'Hungary'
    };
  }

  /**
   * XML escape karakterek
   */
  private escapeXML(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /**
   * XML prettify
   */
  private prettifyXML(xml: string): string {
    // Egyszerű pretty printing - valós implementációban library kellene
    return xml.replace(/></g, '>\n<').replace(/\t/g, '  ');
  }

  /**
   * Gyors HUF generálás
   */
  static generateHUF(
    billingData: ClearAdminBillingData[],
    month?: number,
    year?: number,
    provider?: string
  ): ClearAdminGenerationResult {
    const generator = new ClearAdminGenerator({ currency: 'HUF' });
    return generator.generateXML(billingData, {
      currency: 'HUF',
      month,
      year,
      provider
    });
  }

  /**
   * Gyors EUR generálás
   */
  static generateEUR(
    billingData: ClearAdminBillingData[],
    month?: number,
    year?: number,
    provider?: string
  ): ClearAdminGenerationResult {
    const generator = new ClearAdminGenerator({ currency: 'EUR' });
    return generator.generateXML(billingData, {
      currency: 'EUR',
      month,
      year,
      provider
    });
  }
} 